import { messaging } from '../lib/firebase';
import { getToken, onMessage, deleteToken, type Messaging } from 'firebase/messaging';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { NativeLogger } from './nativeLogger';
import { Capacitor } from '@capacitor/core';

// FCM utility functions
type DeviceTokenKind = 'pc' | 'pwa' | 'android';

export class FCMService {
  private static instance: FCMService;
  private messaging: Messaging | null;

  private constructor() {
    this.messaging = messaging;
  }

  public static getInstance(): FCMService {
    if (!FCMService.instance) {
      FCMService.instance = new FCMService();
    }
    return FCMService.instance;
  }

  // Request permission and get FCM token
  async requestPermission(userId: string): Promise<string | null> {
    console.log('FCM Service: requestPermission called for user:', userId);
    
    if (!this.messaging) {
      console.warn('FCM Service: FCM is not supported in this environment');
      return null;
    }

    try {
      // Detect if current environment is a PWA/browser on a mobile device
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log('FCM Service: Device type (UA):', isMobileUA ? 'Mobile' : 'PC');
      
      if (isMobileUA) {
        console.log('FCM Service: Mobile device detected - requesting notification permission');
        
        // For mobile, we might need to handle PWA installation prompts
        if ('beforeinstallprompt' in window) {
          console.log('FCM Service: PWA install prompt available');
        }
      }

      console.log('FCM Service: Requesting notification permission...');
      
      // Add timeout to permission request in case it hangs
      const permissionPromise = Notification.requestPermission();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Permission request timeout - please try again')), 15000); // 15 second timeout
      });
      
      try {
        const permission = await Promise.race([permissionPromise, timeoutPromise]);
        console.log('FCM Service: Permission result:', permission);
        
        if (permission === 'granted') {
          console.log('FCM Service: Permission granted, getting FCM token...');

          // Ensure we bind to the same service worker registration used for FCM
          let swReg: ServiceWorkerRegistration | undefined;
          try {
            swReg = (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js'))
              || (await navigator.serviceWorker.ready);
            console.log('FCM Service: Using SW registration for token:', swReg?.scope);
          } catch (swErr) {
            console.warn('FCM Service: Could not get SW registration, proceeding without explicit registration', swErr);
          }

          const vapid = (import.meta as any)?.env?.VITE_FIREBASE_VAPID_KEY as string | undefined;
          console.log('FCM Service: Using VAPID key length:', typeof vapid === 'string' ? vapid.length : 'missing');
          if (!vapid || typeof vapid !== 'string') {
            console.warn('FCM Service: VAPID key is missing. Ensure VITE_FIREBASE_VAPID_KEY is set at build time.');
          }

          let token: string | null = null;
          try {
            token = await getToken(this.messaging, {
              vapidKey: vapid,
              serviceWorkerRegistration: swReg,
            });
          } catch (getTokenErr: any) {
            console.error('FCM Service: getToken failed:', getTokenErr?.message || getTokenErr);
            throw getTokenErr;
          }
          console.log('FCM Service: FCM Token obtained:', token ? 'Success' : 'Failed');
          
          // Save token to tokens collection under both 'pc' and 'pwa' kinds so they are identical
          if (token && userId) {
            console.log('FCM Service: Saving web token as both pc and pwa…');
            await Promise.all([
              this.saveTokenToUser(userId, token, 'pc'),
              this.saveTokenToUser(userId, token, 'pwa'),
            ]);
            console.log('FCM Service: Web token saved to pc and pwa');
          } else {
            console.warn('FCM Service: No token received or no userId provided');
          }
          
          return token;
        } else if (permission === 'denied') {
          console.warn('FCM Service: Notification permission denied by user');
          throw new Error('Notification permission was denied. Please enable notifications in your browser settings and try again.');
        } else {
          console.warn('FCM Service: Notification permission dismissed (default)');
          throw new Error('Notification permission was dismissed. Please click "Allow" when prompted to enable notifications.');
        }
      } catch (permissionError) {
        console.error('FCM Service: Permission request failed:', permissionError);
        throw permissionError;
      }
    } catch (error) {
      console.error('FCM Service: Error getting FCM token:', error);
      return null;
    }
  }

  // Save FCM token to tokens collection instead of user document
  async saveTokenToUser(userId: string, token: string, kind: DeviceTokenKind): Promise<void> {
    try {
      NativeLogger.info(`Saving token for user ${userId}, kind: ${kind}, token length: ${token.length}`);
      
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        NativeLogger.warn('User document does not exist, creating it first...');
        // Create the user document if it doesn't exist
        await setDoc(userRef, {
          Name: '', // Will be updated by DBProvider later
          Role: 'user'
        });
        NativeLogger.info('User document created');
      }

      NativeLogger.info('User document exists, saving token to tokens collection...');

      // Save token to tokens collection instead of user document
      const tokenRef = doc(db, 'tokens', `${userId}_${kind}`);
      const tokenData = {
        userId: userId,
        token: token,
        kind: kind,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };
      
      NativeLogger.info('Token document data prepared, writing to Firestore...');
      
      await setDoc(tokenRef, tokenData, { merge: true });

      NativeLogger.info(`✅ Token saved successfully to tokens collection for user ${userId}, kind: ${kind}`);
    } catch (error) {
      NativeLogger.error('❌ Error saving FCM token to tokens collection:', error);
      throw error; // Re-throw to let caller know about the error
    }
  }

  // Listen for incoming messages when app is in foreground
  onMessageReceived(callback: (payload: { notification?: { title?: string; body?: string }; data?: Record<string, string> }) => void) {
    if (!this.messaging) {
      console.warn('FCM is not supported in this environment');
      return;
    }

    return onMessage(this.messaging, (payload) => {
      console.log('Message received:', payload);
      callback(payload);
    });
  }

  // Delete current web token and clean local caches (desktop web only)
  async deleteWebToken(): Promise<boolean> {
    if (!this.messaging) return false;
    try {
      const ok = await deleteToken(this.messaging);
      console.log('FCM Service: deleteToken:', ok);
      return ok;
    } catch (err) {
      console.error('FCM Service: deleteToken failed:', err);
      return false;
    }
  }

  // Full reset of web push setup, then re-request permission and token
  async resetWebPush(userId: string): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    try {
      console.log('FCM Service: Resetting web push…');
      // 1) Delete current web token (if any)
      await this.deleteWebToken();

      // 2) Unregister SW (firebase-messaging-sw.js)
      try {
        const reg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        if (reg) {
          console.log('FCM Service: Unregistering SW:', reg.scope);
          await reg.unregister();
        }
      } catch (swErr) {
        console.warn('FCM Service: Failed to unregister SW:', swErr);
      }

      // 3) Clear Firebase Messaging/Installations IndexedDB stores
      try {
        const dbs = [
          'firebase-messaging-database',
          'firebase-installations-database',
        ];
        for (const name of dbs) {
          try {
            await new Promise<void>((resolve, reject) => {
              const req = indexedDB.deleteDatabase(name);
              req.onsuccess = () => resolve();
              req.onerror = () => reject(req.error);
              req.onblocked = () => resolve();
            });
            console.log('FCM Service: Deleted IDB:', name);
          } catch (e) {
            console.warn('FCM Service: Failed to delete IDB', name, e);
          }
        }
      } catch (e) {
        console.warn('FCM Service: Error clearing IndexedDB caches:', e);
      }

      // 4) Clear runtime caches that may hold old SW assets
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          for (const key of keys) {
            if (/firebase|messaging|workbox|vite|astroshifts/i.test(key)) {
              await caches.delete(key);
              console.log('FCM Service: Deleted cache:', key);
            }
          }
        }
      } catch (e) {
        console.warn('FCM Service: Failed to clear caches:', e);
      }

      // 5) Re-register SW by waiting for page ready SW (main.tsx registers)
      try {
        await new Promise((r) => setTimeout(r, 500));
        const ready = await navigator.serviceWorker.ready.catch(() => undefined);
        console.log('FCM Service: SW ready after reset:', !!ready);
      } catch {}

      // 6) Re-request permission and token
      if (Notification.permission === 'denied') {
        throw new Error('Notifications are blocked. Please allow notifications in your browser settings.');
      }
      return await this.requestPermission(userId);
    } catch (err) {
      console.error('FCM Service: resetWebPush failed:', err);
      return null;
    }
  }

  // Daily reminders subscribe/unsubscribe removed for now

  // Silently refresh token on login (no permission prompts)
  async refreshTokenOnLogin(userId: string): Promise<string | null> {
    if (!userId) return null;
    try {
      if (Capacitor.isNativePlatform()) {
        try {
          const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
          const tokenResult: any = await FirebaseMessaging.getToken();
          const token: string | undefined = tokenResult?.token || tokenResult?.fcmToken || tokenResult?.value;
          if (token) {
            await this.saveTokenToUser(userId, token, 'android');
            return token;
          }
        } catch (e) {
          console.warn('FCM Service: Native login refresh failed to get token:', e);
        }
        return null;
      }

      // Web: only refresh if permission already granted
      return await this.refreshWebTokenIfGranted(userId);
    } catch (e) {
      console.warn('FCM Service: refreshTokenOnLogin error:', e);
      return null;
    }
  }

  // Web-only: if permission is granted, getToken and save without prompting
  private async refreshWebTokenIfGranted(userId: string): Promise<string | null> {
    if (!this.messaging) return null;
    if (typeof window === 'undefined') return null;
    if (!('Notification' in window)) return null;
    if (Notification.permission !== 'granted') return null;
    try {
      let swReg: ServiceWorkerRegistration | undefined;
      try {
        swReg = (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js'))
          || (await navigator.serviceWorker.ready);
      } catch {}
      const vapid = (import.meta as any)?.env?.VITE_FIREBASE_VAPID_KEY as string | undefined;
      const token = await getToken(this.messaging, { vapidKey: vapid, serviceWorkerRegistration: swReg });
      if (!token) return null;
      await Promise.all([
        this.saveTokenToUser(userId, token, 'pc'),
        this.saveTokenToUser(userId, token, 'pwa'),
      ]);
      return token;
    } catch (e) {
      console.warn('FCM Service: refreshWebTokenIfGranted getToken failed:', e);
      return null;
    }
  }

  // Send custom notification (for testing)
  async sendCustomNotification(title: string, body: string, token: string): Promise<boolean> {
    try {
      const response = await fetch('/.netlify/functions/sendCustomNotification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title, body, token }),
      });

      if (response.ok) {
        console.log('Custom notification sent successfully');
        return true;
      } else {
        console.error('Failed to send custom notification');
        return false;
      }
    } catch (error) {
      console.error('Error sending custom notification:', error);
      return false;
    }
  }

  // Send instant notification
  async sendInstantNotification(userId: string, target?: 'pc' | 'pwa' | 'android' | 'all'): Promise<boolean> {
    if (!userId) {
      console.error('No user ID provided for instant notification');
      return false;
    }

    console.log('FCM Service: Sending instant notification for user:', userId);
    
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const sendInstantNotificationFn = httpsCallable(functions, 'sendInstantNotification');

      console.log('FCM Service: Calling Firebase function...');
      const functionData = target ? { target } : {};
      console.log('FCM Service: Sending data to function:', functionData);
      const result = await sendInstantNotificationFn(functionData);

      console.log('FCM Service: Function result:', result);
      
      if (result.data && (result.data as any).success) {
        console.log('FCM Service: Successfully sent instant notification');
        return true;
      } else {
        console.error('FCM Service: Failed to send instant notification:', result.data);
        return false;
      }
    } catch (error) {
      console.error('FCM Service: Error sending instant notification:', error);
      return false;
    }
  }
}

// Export singleton instance
export const fcmService = FCMService.getInstance();
